import { WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { WorkflowFieldMetaDataV2, WorkflowFieldV2 } from "@/types/WorkflowCreate/workflowDetailsV2";
import { FieldMapping } from "@/types/WorkflowCreate/workflowCreate"
import { App } from "@/types/applications";
import { WorkflowField, WorkflowFieldMetadataUpdate, WorkflowFieldMappingUpdate, WorkflowTask } from "@/types/WorkflowCreate/workflowUpdateV2";
import { FieldMappingOption } from "@/types/workflow";

interface WorkflowFieldDiffEntry {
  id?: string
  sourceField: string
  destinationField: string
  sourceTaskId?: string
  destinationTaskId?: string
  sourceTaskOrder?: number
  destinationTaskOrder?: number
  sourceAppId?: string
  destinationAppId?: string
  order: number
  iterateThrough?: string
  options?: FieldMappingOption
  remove?: boolean
}

interface WorkflowFieldMetadataCandidate {
  sourceTaskId?: string
  destinationTaskId?: string
  sourceTaskOrder?: number
  destinationTaskOrder?: number
  sourceAppId?: string
  destinationAppId?: string
  iterateThrough?: string
}

interface FlattenWorkflowFieldMappingsResult {
  fields: WorkflowFieldDiffEntry[]
  metadataCandidates: WorkflowFieldMetadataCandidate[]
}

const getSourceTaskOrder = (field: WorkflowFieldDiffEntry | WorkflowFieldV2) =>
  (field as WorkflowFieldDiffEntry).sourceTaskOrder;

const getDestinationTaskOrder = (field: WorkflowFieldDiffEntry | WorkflowFieldV2) =>
  (field as WorkflowFieldDiffEntry).destinationTaskOrder;

const fallbackDestinationKey = (field: WorkflowFieldDiffEntry | WorkflowFieldV2) => {
  if (field.destinationTaskId) return field.destinationTaskId;
  const order = getDestinationTaskOrder(field);
  if (typeof order === "number") return `order:${order}`;
  if (field.destinationAppId) return `app:${field.destinationAppId}`;
  return "unknown-destination";
};

const buildDestinationGroupKey = (field: WorkflowFieldDiffEntry | WorkflowFieldV2) =>
  `${fallbackDestinationKey(field)}_${field.iterateThrough ?? "none"}`;

const normalizeFieldKey = (f: WorkflowFieldDiffEntry | WorkflowFieldV2) =>
  f.id ?? `${f.sourceField}_${f.destinationField}_${f.sourceTaskId ?? getSourceTaskOrder(f) ?? f.sourceAppId ?? "src-null"}_${f.destinationTaskId ?? getDestinationTaskOrder(f) ?? f.destinationAppId ?? "dest-null"}`;

export const isEqual = (a?: FieldMappingOption, b?: FieldMappingOption) => JSON.stringify(a) === JSON.stringify(b);

export const flattenWorkflowFieldMappings = (
  mappings: FieldMapping[],
  applications: App[],
  wfSteps: WorkflowTask[]
): FlattenWorkflowFieldMappingsResult => {
  type WorkflowTaskWithType = WorkflowTask & { stepType?: WorkflowStepType | string };
  const activeSteps = wfSteps.filter(step => !step.remove);
  const stepsByOrder = new Map<number, WorkflowTaskWithType>();
  activeSteps.forEach(step => stepsByOrder.set(step.order, step as WorkflowTaskWithType));

  const parseOrder = (value: string): number | undefined => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const findStepByOrder = (orderToken: string) => {
    const parsedOrder = parseOrder(orderToken);
    if (parsedOrder === undefined) return undefined;
    return stepsByOrder.get(parsedOrder) ?? activeSteps.find(step => step.order === parsedOrder);
  };

  const resolveAppIdByName = (appName?: string) =>
    applications.find(app => app.name === appName)?.id;

  const flattened: WorkflowFieldDiffEntry[] = [];
  const metadataByPair = new Map<string, WorkflowFieldMetadataCandidate>();

  mappings.forEach(item => {
    item.mappings.forEach(m => {
      const [sourceAppWithStep, sourceFieldName] = m.dataSourceFieldId.split('=>');
      const [sourceStepOrder, sourceApp] = sourceAppWithStep.split(';');
      const [destinationStepOrder, destinationAppId] = item.destinationId.split(';');

      const sourceStep = findStepByOrder(sourceStepOrder);
      const destinationStep = findStepByOrder(destinationStepOrder);

      if (!sourceStep || !destinationStep) {
        console.warn("Step mapping failed:", { sourceStepOrder, destinationStepOrder, sourceApp, destinationAppId });
        return;
      }

      const isTransformationSource =
        (sourceStep as WorkflowTaskWithType)?.stepType === WorkflowStepType.TRANSFORMATION ||
        sourceApp === "transformation";

      const resolvedSourceField = m.customSourceLabel?.trim()?.length
        ? m.customSourceLabel.trim()
        : sourceFieldName;

      const resolvedSourceAppId = isTransformationSource
        ? undefined
        : sourceStep.appId ?? resolveAppIdByName(sourceApp);

      const resolvedDestinationAppId = destinationStep.appId ?? destinationAppId;
      const iterateThroughValue = item.iterateThrough;

      flattened.push({
        sourceField: resolvedSourceField,
        destinationField: m.fieldName,
        order: m.order,
        options: m.options,
        iterateThrough: iterateThroughValue,
        sourceTaskId: sourceStep.id,
        destinationTaskId: destinationStep.id,
        sourceTaskOrder: sourceStep.order,
        destinationTaskOrder: destinationStep.order,
        sourceAppId: resolvedSourceAppId,
        destinationAppId: resolvedDestinationAppId
      } as WorkflowFieldDiffEntry);

      const metadataCandidate: WorkflowFieldMetadataCandidate = {
        sourceTaskId: sourceStep.id,
        destinationTaskId: destinationStep.id,
        sourceTaskOrder: sourceStep.order,
        destinationTaskOrder: destinationStep.order,
        sourceAppId: resolvedSourceAppId,
        destinationAppId: resolvedDestinationAppId,
        iterateThrough: iterateThroughValue
      };

      if (iterateThroughValue) {
        const metadataKey = buildMetadataPairKey(metadataCandidate);
        if (!metadataByPair.has(metadataKey)) {
          metadataByPair.set(metadataKey, metadataCandidate);
        } else {
          const existing = metadataByPair.get(metadataKey);
          if (existing) {
            existing.iterateThrough = metadataCandidate.iterateThrough;
          }
        }
      }
    });
  });

  return {
    fields: flattened,
    metadataCandidates: Array.from(metadataByPair.values())
  };
}

const reuseDestinationIds = (newFields: WorkflowFieldDiffEntry[], oldFields: WorkflowFieldV2[]) => {
  const oldGroups = new Map<string, WorkflowFieldV2[]>();
  oldFields.forEach(field => {
    const key = buildDestinationGroupKey(field);
    const bucket = oldGroups.get(key);
    if (bucket) {
      bucket.push(field);
    } else {
      oldGroups.set(key, [field]);
    }
  });

  const newGroups = new Map<string, WorkflowFieldDiffEntry[]>();
  newFields.forEach(field => {
    const key = buildDestinationGroupKey(field);
    const bucket = newGroups.get(key);
    if (bucket) {
      bucket.push(field);
    } else {
      newGroups.set(key, [field]);
    }
  });

  for (const [key, group] of newGroups.entries()) {
    const oldGroup = oldGroups.get(key);
    if (!oldGroup || oldGroup.length !== group.length) continue;

    const sortedOld = [...oldGroup].sort((a, b) => a.order - b.order);
    const sortedNew = [...group].sort((a, b) => a.order - b.order);

    sortedNew.forEach((field, index) => {
      const reference = sortedOld[index];
      field.id = reference.id;
      field.sourceTaskId = field.sourceTaskId ?? reference.sourceTaskId;
      field.destinationTaskId = field.destinationTaskId ?? reference.destinationTaskId;
      field.sourceAppId = field.sourceAppId ?? reference.sourceAppId;
      field.destinationAppId = field.destinationAppId ?? reference.destinationAppId;
    });
  }
};

const convertOldField = (field: WorkflowFieldV2): WorkflowFieldDiffEntry => ({
  id: field.id,
  sourceField: field.sourceField,
  destinationField: field.destinationField,
  sourceTaskId: field.sourceTaskId,
  destinationTaskId: field.destinationTaskId,
  sourceAppId: field.sourceAppId,
  destinationAppId: field.destinationAppId,
  order: field.order,
  iterateThrough: field.iterateThrough,
  options: field.options
});

const fieldsAreDifferent = (oldField: WorkflowFieldV2, newField: WorkflowFieldDiffEntry) => (
  oldField.sourceField !== newField.sourceField ||
  oldField.destinationField !== newField.destinationField ||
  oldField.sourceTaskId !== newField.sourceTaskId ||
  oldField.destinationTaskId !== newField.destinationTaskId ||
  (oldField.sourceAppId) !== (newField.sourceAppId) ||
  (oldField.destinationAppId) !== (newField.destinationAppId) ||
  oldField.order !== newField.order ||
  oldField.iterateThrough !== newField.iterateThrough ||
  !isEqual(oldField.options, newField.options)
);

const buildWorkflowFieldDiff = (
  oldFields: WorkflowFieldV2[],
  newFields: WorkflowFieldDiffEntry[]
): WorkflowFieldDiffEntry[] => {
  reuseDestinationIds(newFields, oldFields);

  const oldMap = new Map(oldFields.map((f) => [normalizeFieldKey(f), f]));
  const newMap = new Map(newFields.map((f) => [normalizeFieldKey(f), f]));

  const added: WorkflowFieldDiffEntry[] = [];
  const removed: WorkflowFieldDiffEntry[] = [];
  const updated: WorkflowFieldDiffEntry[] = [];

  for (const [key, oldField] of oldMap.entries()) {
    const newField = newMap.get(key);
    if (!newField) {
      removed.push({
        ...convertOldField(oldField),
        remove: true
      });
    } else if (fieldsAreDifferent(oldField, newField)) {
      updated.push({
        ...newField,
        id: oldField.id,
      });
    }
  }

  for (const [key, newField] of newMap.entries()) {
    if (!oldMap.has(key)) {
      const fieldWithoutId: WorkflowFieldDiffEntry = { ...newField };
      delete fieldWithoutId.id;
      added.push(fieldWithoutId);
    }
  }

  return [
    ...added,
    ...removed,
    ...updated
  ];
};

const getMetadataEndpointKey = (
  meta: WorkflowFieldMetaDataV2 | WorkflowFieldMetadataCandidate,
  role: "source" | "destination"
) => {
  const taskId = role === "source" ? meta.sourceTaskId : meta.destinationTaskId;
  if (taskId) return `task:${taskId}`;

  if (role === "source" && "sourceTaskOrder" in meta && typeof meta.sourceTaskOrder === "number") {
    return `order:${meta.sourceTaskOrder}`;
  }

  if (role === "destination" && "destinationTaskOrder" in meta && typeof meta.destinationTaskOrder === "number") {
    return `order:${meta.destinationTaskOrder}`;
  }

  if (role === "source" && "sourceAppId" in meta && meta.sourceAppId) {
    return `app:${meta.sourceAppId}`;
  }

  if (role === "destination" && "destinationAppId" in meta && meta.destinationAppId) {
    return `app:${meta.destinationAppId}`;
  }

  return role === "source" ? "source:unknown" : "destination:unknown";
};

const buildMetadataPairKey = (meta: WorkflowFieldMetaDataV2 | WorkflowFieldMetadataCandidate) => (
  `${getMetadataEndpointKey(meta, "source")}->${getMetadataEndpointKey(meta, "destination")}`
);

const convertCandidateToMetadataUpdate = (
  candidate: WorkflowFieldMetadataCandidate
): WorkflowFieldMetadataUpdate => ({
  sourceTaskId: candidate.sourceTaskId,
  destinationTaskId: candidate.destinationTaskId,
  sourceAppOrder: candidate.sourceTaskOrder,
  destinationAppOrder: candidate.destinationTaskOrder,
  sourceAppId: candidate.sourceAppId,
  destinationAppId: candidate.destinationAppId,
  iterateThrough: candidate.iterateThrough
});

const buildWorkflowFieldMetadataDiff = (
  oldMetadata: WorkflowFieldMetaDataV2[],
  newMetadata: WorkflowFieldMetadataCandidate[]
): WorkflowFieldMetadataUpdate[] => {
  const oldMap = new Map<string, WorkflowFieldMetaDataV2>();
  oldMetadata.forEach(meta => {
    const key = buildMetadataPairKey(meta);
    if (!oldMap.has(key)) {
      oldMap.set(key, meta);
    }
  });

  const newMap = new Map<string, WorkflowFieldMetadataCandidate>();
  newMetadata.forEach(candidate => {
    const key = buildMetadataPairKey(candidate);
    if (!newMap.has(key)) {
      newMap.set(key, candidate);
    }
  });

  const diff: WorkflowFieldMetadataUpdate[] = [];

  for (const [key, oldMeta] of oldMap.entries()) {
    const candidate = newMap.get(key);
    if (!candidate) {
      diff.push({ id: oldMeta.id, remove: true });
      continue;
    }

    const normalizedIterate = candidate.iterateThrough;
    if ((oldMeta.iterateThrough ?? null) !== normalizedIterate) {
      diff.push({
        id: oldMeta.id,
        iterateThrough: normalizedIterate
      });
    }
  }

  for (const [key, candidate] of newMap.entries()) {
    if (oldMap.has(key)) continue;
    diff.push(convertCandidateToMetadataUpdate(candidate));
  }

  return diff;
};

export const getFieldMappingDifference = (
  oldFields: WorkflowFieldV2[],
  newFieldMappings: FieldMapping[],
  applications: App[],
  wfSteps: WorkflowTask[]
): WorkflowFieldDiffEntry[] => {
  const { fields } = flattenWorkflowFieldMappings(newFieldMappings, applications, wfSteps);
  return buildWorkflowFieldDiff(oldFields, fields);
}

export const getFieldMappingsAndMetadataDifference = (
  oldFields: WorkflowFieldV2[],
  oldMetadata: WorkflowFieldMetaDataV2[],
  newFieldMappings: FieldMapping[],
  applications: App[],
  wfSteps: WorkflowTask[]
) => {
  const { fields, metadataCandidates } = flattenWorkflowFieldMappings(newFieldMappings, applications, wfSteps);

  return {
    workflowFields: buildWorkflowFieldDiff(oldFields, fields),
    workflowFieldMetadata: buildWorkflowFieldMetadataDiff(oldMetadata, metadataCandidates)
  };
}

const getDiffEndpointKey = (
  field: WorkflowFieldDiffEntry,
  role: "source" | "destination"
) => {
  const taskId = role === "source" ? field.sourceTaskId : field.destinationTaskId;
  if (taskId) return `task:${taskId}`;

  const order = role === "source" ? field.sourceTaskOrder : field.destinationTaskOrder;
  if (typeof order === "number") return `order:${order}`;

  const appId = role === "source" ? field.sourceAppId : field.destinationAppId;
  if (appId) return `app:${appId}`;

  return role === "source" ? "source:unknown" : "destination:unknown";
};

const buildFieldGroupKey = (field: WorkflowFieldDiffEntry) => (
  `${getDiffEndpointKey(field, "source")}->${getDiffEndpointKey(field, "destination")}::${field.iterateThrough ?? "none"}`
);

const createWorkflowFieldGroup = (field: WorkflowFieldDiffEntry): WorkflowField => {
  const group: WorkflowField = {
    mappings: []
  };

  if (field.sourceTaskId) {
    group.sourceTaskId = field.sourceTaskId;
  } else {
    if (typeof field.sourceTaskOrder === "number") {
      group.sourceAppOrder = field.sourceTaskOrder;
    }
    if (field.sourceAppId) {
      group.sourceAppId = field.sourceAppId;
    }
  }

  if (field.destinationTaskId) {
    group.destinationTaskId = field.destinationTaskId;
  } else {
    if (typeof field.destinationTaskOrder === "number") {
      group.destinationAppOrder = field.destinationTaskOrder;
    }
    if (field.destinationAppId) {
      group.destinationAppId = field.destinationAppId;
    }
  }

  return group;
};

const createWorkflowFieldMappingPayload = (
  field: WorkflowFieldDiffEntry
): WorkflowFieldMappingUpdate => {
  const payload: WorkflowFieldMappingUpdate = {
    id: field.id,
    remove: field.remove
  };

  if (!field.remove) {
    payload.sourceField = field.sourceField;
    payload.destinationField = field.destinationField;
    payload.order = field.order;
    if (field.options !== undefined) {
      payload.options = field.options;
    }
  }

  if (field.remove && typeof field.order === "number" && payload.order === undefined) {
    payload.order = field.order;
  }

  return payload;
};

export const groupWorkflowFieldDiffs = (
  fields: WorkflowFieldDiffEntry[]
): WorkflowField[] => {
  const grouped = new Map<string, WorkflowField>();

  fields.forEach(field => {
    const key = buildFieldGroupKey(field);
    const existingGroup = grouped.get(key);
    const targetGroup = existingGroup ?? createWorkflowFieldGroup(field);
    targetGroup.mappings.push(createWorkflowFieldMappingPayload(field));
    grouped.set(key, targetGroup);
  });

  return Array.from(grouped.values()).map(group => ({
    ...group,
    mappings: [...group.mappings].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  }));
};
